import * as validator from 'card-validator';
import { SbpsCreditCardTokenGenerator } from './SbpsCreditCardTokenGenerator';

// see app/views/layouts/_spbs_credit_token.html.slim
declare const sbpsConfig: any;

class CreditCardErrors {
  messages: { [key: string]: string };
  errorMessageMap: { [key: string]: { [key: string]: string } };

  constructor({
    messages = {},
    errorMessageMap,
  }: {
    messages?: { [key: string]: string };
    errorMessageMap: { [key: string]: { [key: string]: string } };
  }) {
    this.messages = messages;
    this.errorMessageMap = errorMessageMap;
  }

  // We only say that it is invalid in almost case because we can't validate credit attributes strictly.
  add(item: string, message: string = 'invalid') {
    this.messages[item] = message;
  }

  fullMessages() {
    return Object.keys(this.messages).map(
      (item) => this.errorMessageMap[item][this.messages[item]],
    );
  }

  items() {
    return Object.keys(this.messages);
  }

  isEmpty() {
    return this.items().length === 0;
  }
}

export class CreditCard {
  // https://github.com/braintree/credit-card-type#creditcardtypenumber-string
  // https://github.com/quipper/aya-issues/issues/4330#issuecomment-317389074
  private static BRAND_TYPE_VISA = 'visa';
  private static BRAND_TYPE_JCB = 'jcb';
  private static BRAND_TYPE_MASTERCARD = 'mastercard';
  private static BRAND_TYPE_DINERS = 'diners-club';
  private static BRAND_TYPE_AMEX = 'american-express';
  private static ALLOWED_BRAND_TYPES = [
    CreditCard.BRAND_TYPE_VISA,
    CreditCard.BRAND_TYPE_JCB,
    CreditCard.BRAND_TYPE_MASTERCARD,
    CreditCard.BRAND_TYPE_DINERS,
    CreditCard.BRAND_TYPE_AMEX,
  ];

  // correspond with credit_card.rb token_error_messages's key
  private static ATTR_NAME_NUMBER = 'card_number';
  private static ATTR_NAME_BRAND = 'card_brand';
  private static ATTR_NAME_EXPIRATION = 'card_expiration';
  private static ATTR_NAME_SECURITY_CODE = 'card_security_code';

  number: string;
  expirationYear: string;
  expirationMonth: string;
  securityCode: string;
  errors: CreditCardErrors;
  tokenGenerator: SbpsCreditCardTokenGenerator;

  // TODO: Convert Zenkaku to Hankaku. https://github.com/quipper/aya-issues/issues/14660
  constructor({
    number,
    expirationYear,
    expirationMonth,
    securityCode,
    errorMessageMap,
  }: {
    number: string;
    expirationYear: string;
    expirationMonth: string;
    securityCode: string;
    errorMessageMap: { [key: string]: { [key: string]: string } };
  }) {
    this.tokenGenerator = new SbpsCreditCardTokenGenerator({
      tokenLib: sbpsConfig.creditCardTokenLib,
      merchantId: sbpsConfig.merchantId,
      serviceId: sbpsConfig.serviceId,
    });
    this.number = number?.replace(/[^\d]/g, '') || ''; // trim hyphen if needed
    this.expirationYear = expirationYear;
    this.expirationMonth = expirationMonth;
    this.securityCode = securityCode;
    this.errors = new CreditCardErrors({ errorMessageMap });
  }

  isValid() {
    const validatedNumber = validator.number(this.number);
    const brandType = validatedNumber.card?.type || '';

    if (!validatedNumber.isValid) {
      this.errors.add(CreditCard.ATTR_NAME_NUMBER);
    }

    // brandType depends on number so we must ensure that number is valid
    if (
      validatedNumber.isValid &&
      !CreditCard.ALLOWED_BRAND_TYPES.includes(brandType)
    ) {
      this.errors.add(CreditCard.ATTR_NAME_BRAND);
    }

    if (
      !validator.expirationDate({
        year: this.expirationYear,
        month: this.expirationMonth,
      }).isValid
    ) {
      this.errors.add(CreditCard.ATTR_NAME_EXPIRATION);
    }

    if (
      !validator.cvv(this.securityCode, this.securityCodeMaxLength(brandType))
        .isValid
    ) {
      this.errors.add(CreditCard.ATTR_NAME_SECURITY_CODE);
    }

    // Overwrite error message if it is blank (The existing validation is most highest priority)
    const itemValueMap: { [key: string]: string } = {};
    itemValueMap[CreditCard.ATTR_NAME_NUMBER] = this.number;
    itemValueMap[CreditCard.ATTR_NAME_EXPIRATION] =
      this.expirationYear || this.expirationMonth;
    itemValueMap[CreditCard.ATTR_NAME_SECURITY_CODE] = this.securityCode;

    for (const [item, value] of Object.entries(itemValueMap)) {
      if (!value) {
        this.errors.add(item, 'blank');
      }
    }

    return this.errors.isEmpty();
  }

  securityCodeMaxLength(brandType: string) {
    return brandType === CreditCard.BRAND_TYPE_AMEX ? 4 : 3;
  }

  generateToken() {
    if (!this.isValid()) {
      throw new Error(`creditCard is invalid [${this.errors}]`);
    }
    return this.tokenGenerator.generate(this);
  }
}
